import { useEffect, useRef, useState } from 'react';

import Image from 'next/image';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';

import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import LinkIcon from '@mui/icons-material/Link';
import ReplyIcon from '@mui/icons-material/Reply';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import ThumbDownAlt from '@mui/icons-material/ThumbDownAlt';
import ThumbDownOffAlt from '@mui/icons-material/ThumbDownOffAlt';
import ThumbUpAlt from '@mui/icons-material/ThumbUpAlt';
import ThumbUpOffAlt from '@mui/icons-material/ThumbUpOffAlt';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import MuiLink from '@mui/material/Link';
import Typography from '@mui/material/Typography';

import { InfoCard, LinkCard } from '@/components/cards';
import { useCompose } from '@/context/ComposeContext';
import { useSnackbar } from '@/context/SnackbarContext';
import { useUserInfo } from '@/context/UserInfoContext';
import {
  BgTaskStatus,
  castVote,
  DisclosedVoteInfo,
  DOC_VISIBILITY,
  DisplayDoc,
  DocNode,
  DocVisibility,
  VoteAction,
  VoteState,
} from '@/lib/api';
import { API_URL, WEBSOCKET_URL } from '@/lib/consts';
import { ASPECT_QUESTION, ASPECT_REVIEW } from '@/lib/consts';
import { isLLM } from '@/lib/utils';

import { ComposeBox } from './ComposeBox';
import styles from './DocCard.module.css';
import { StructuredDocRenderer } from './StructuredDocRenderer';
import { VisibilityIcon } from './Visibility';

const scoreFormatter = new Intl.NumberFormat(undefined, { signDisplay: 'exceptZero' });
const formatScore = (score: number): string => scoreFormatter.format(score);
type VoteInfoSnapshot = Pick<DisclosedVoteInfo, 'score' | 'user_vote'> | null;

const voteInfoSnapshotsEqual = (left: VoteInfoSnapshot, right: VoteInfoSnapshot): boolean =>
  left?.score === right?.score && left?.user_vote === right?.user_vote;

// Timeout duration for processing status messages. Set to 30 seconds to balance between
// giving the AI enough time to process complex requests while preventing users from
// thinking the app is frozen during failed operations
const PROCESSING_TIMEOUT_MS = 30000;

interface DocCardProps {
  doc: DisplayDoc; // Note: this used to be DocNode, but that doesn't seem to be required...?
  mutate: () => void;
  inheritedVisibility?: DocVisibility;
  // TODO(2): normalize some of the following fields, e.g., combine isMainDoc
  // and isMainDocChild, maybe some others, into the context field.
  context: 'thread' | 'standalone';
  isMainDoc?: boolean; // Only applies to 'thread'.
  isMainDocChild?: boolean; // Indicates if this doc is a direct child of the main doc
  clickable?: boolean; // Controls whether the entire card is clickable in standalone mode
  isInFeedbackSubtree?: boolean; // Indicates if this doc is part of a feedback subtree
  threadMarker?: React.ReactNode; // Optional visual marker to show thread position
  indentationElement?: React.ReactNode; // Optional element for custom indentation
}

/** Renders a single document card in thread and standalone contexts. */
const DocCard: React.FC<DocCardProps> = ({
  doc,
  isMainDoc = false,
  isMainDocChild = false,
  mutate,
  inheritedVisibility,
  context,
  clickable = true,
  isInFeedbackSubtree = false,
  threadMarker,
  indentationElement,
}) => {
  if (context !== 'thread' && context !== 'standalone') {
    throw new Error('DocCard context must be either "thread" or "standalone"');
  } else if (context !== 'thread' && isMainDoc) {
    throw new Error('Main doc can only be used in a thread context');
  }

  const [processingStatus, setProcessingStatus] = useState<{
    message: string | null;
    animate: boolean;
  }>({ message: null, animate: false });
  const [socketGeneration, setSocketGeneration] = useState(0);
  const [isComposeVisible, setIsComposeVisible] = useState(false);
  const router = useRouter();
  const searchParams = useSearchParams();
  const cardRef = useRef<HTMLDivElement>(null);
  const { userInfo, reloadUserInfo } = useUserInfo();
  const { setIsComposing, replyToDocId, setReplyToDocId } = useCompose();
  const { showSnackbar } = useSnackbar();
  const [isInCollection, setIsInCollection] = useState(false);
  const [isVoting, setIsVoting] = useState(false);

  // Vote state management: use prop as source of truth with optimistic update overlay.
  // Track the prop value that our optimistic state is based on. When the prop changes,
  // the optimistic state becomes stale and we fall back to the new prop value.
  // We compare by score/user_vote values instead of object identity to handle parent
  // rerenders that recreate the doc object with the same data.
  const propVoteInfo = doc.vote_info ?? null;
  const propVoteSnapshot: VoteInfoSnapshot = propVoteInfo
    ? { score: propVoteInfo.score, user_vote: propVoteInfo.user_vote }
    : null;

  const [optimisticState, setOptimisticState] = useState<{
    baseProp: VoteInfoSnapshot;
    value: DisclosedVoteInfo | null;
  } | null>(null);

  const optimisticStateMatchesProp =
    optimisticState !== null && voteInfoSnapshotsEqual(optimisticState.baseProp, propVoteSnapshot);

  // Derive effective vote info: use optimistic value only if it's based on the current prop
  const voteInfo = optimisticStateMatchesProp ? optimisticState.value : propVoteInfo;

  useEffect(() => {
    if (optimisticState && !optimisticStateMatchesProp) {
      setOptimisticState(null);
    }
  }, [optimisticState, optimisticStateMatchesProp]);

  const isOwnPost = userInfo?.username === doc.provenance.creator;
  const processingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const mutateRef = useRef(mutate);

  // Helper function to set animated status with automatic timeout
  const setAnimatedStatus = (message: string) => {
    // Clear any existing timeout
    if (processingTimeoutRef.current) {
      clearTimeout(processingTimeoutRef.current);
      processingTimeoutRef.current = null;
    }

    // Set the new status with animation
    setProcessingStatus({ message, animate: true });

    // Set timeout to clear the status
    processingTimeoutRef.current = setTimeout(() => {
      setProcessingStatus({ message: null, animate: false });
      processingTimeoutRef.current = null;
    }, PROCESSING_TIMEOUT_MS);
  };

  useEffect(() => {
    mutateRef.current = mutate;
  }, [mutate]);

  useEffect(() => {
    if (userInfo && doc.id) {
      const inCollection = userInfo.collections.some((collection) =>
        collection.docs.includes(doc.id)
      );
      setIsInCollection(inCollection);
    }
  }, [userInfo, doc.id]);

  // Handle compose trigger from OmniBox or inline Reply buttons
  useEffect(() => {
    if (replyToDocId === doc.id && !isComposeVisible) {
      setIsComposeVisible(true);
      setIsComposing(true); // Signal to other components (OmniBox, etc.) that compose is open
      // Clear the trigger so it doesn't reopen if component re-renders
      setReplyToDocId(null);

      // Scroll to this card after a short delay to ensure compose box is rendered
      setTimeout(() => {
        if (cardRef.current) {
          cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
      }, 100);
    }
  }, [replyToDocId, doc.id, isComposeVisible, setReplyToDocId, setIsComposing]);

  const handleStarClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!userInfo || !doc.id) return;

    // In standalone mode, show confirmation prompt (since unstarring causes
    // the item to immediately disappear).  TODO(2): nicer confirmation box
    if (context === 'standalone') {
      const action = isInCollection ? 'remove from' : 'add to';
      if (!window.confirm(`Are you sure you want to ${action} your favorites?`)) {
        return;
      }
    }

    try {
      const action = isInCollection ? 'remove' : 'add';
      const response = await fetch(`${API_URL}/user/update_collections`, {
        credentials: 'include',
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: '',
          action: action,
          docs: [doc.id],
          products: [],
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to update collection');
      }

      // Update the global user info state which will trigger the useEffect above
      await reloadUserInfo();
    } catch (error) {
      console.error('Error updating collection:', error);
    }
  };

  const handleVoteClick = async (clickedState: VoteState) => {
    if (!voteInfo || !doc.id || isOwnPost || isVoting) return;

    // Toggle: if already voted this way, remove; otherwise set the corresponding action
    // Map state to action: 'up' -> 'upvote', 'down' -> 'downvote'
    const stateToAction: Record<VoteState, VoteAction> = { up: 'upvote', down: 'downvote' };
    const newVoteAction: VoteAction =
      voteInfo.user_vote === clickedState ? 'remove' : stateToAction[clickedState];

    setIsVoting(true);
    try {
      const result = await castVote(doc.id, newVoteAction);
      switch (result.status) {
        case 'success':
          if (result.vote_info) {
            // Store the optimistic result along with a snapshot of the prop values it's based on
            setOptimisticState({ baseProp: propVoteSnapshot, value: result.vote_info });
          }
          break;
        case 'rate_limited':
          showSnackbar({ severity: 'warning', message: 'Vote limit reached. Try again later.' });
          break;
        case 'self_vote_not_allowed':
          // Should not happen since we check isOwnPost, but handle gracefully
          showSnackbar({ severity: 'warning', message: 'Cannot vote on your own post.' });
          break;
        case 'doc_not_found':
          showSnackbar({ severity: 'error', message: 'Document not found.' });
          break;
      }
    } catch (error) {
      console.error('Error casting vote:', error);
    } finally {
      setIsVoting(false);
    }
  };

  useEffect(() => {
    if (isMainDoc && cardRef.current) {
      cardRef.current.scrollIntoView({ behavior: 'instant', block: 'start' });
    }
  }, [isMainDoc]);

  useEffect(() => {
    if (!isMainDoc || !doc.id) {
      return;
    }

    if (searchParams.get('processing_started') === '1') {
      // `processing_started` is a one-shot hint used to show immediate
      // acknowledgement after navigation. Ongoing progress comes from the
      // live `/doc/updates/{docid}` websocket, so consume the hint once and
      // remove it from the URL.
      //
      // Using `window.history.replaceState()` here avoids a Next.js router
      // navigation/re-render cycle just to strip the consumed query param.
      // A browser refresh after this point loses the one-shot hint, but the
      // websocket reconnects and resumes showing subsequent task updates.
      setAnimatedStatus('Starting AI analysis');

      const url = new URL(window.location.href);
      url.searchParams.delete('processing_started');
      window.history.replaceState(window.history.state, '', url.toString());
    }
  }, [doc.id, isMainDoc, searchParams]);

  useEffect(() => {
    // When someone posts, they are redirected to the URL for that post;
    // at the same time the backend should be analyzing the new post to
    // determine whether the LLM will post a response.  This websocket
    // tries to watch the status of that analysis.
    if (!isMainDoc) {
      // console.log("Doc id ", doc.id, " is not the main doc, not listening for updates");
      return; // Only listen for updates on the main document
    }

    // Ignore late socket callbacks after this effect has been cleaned up.
    let disposed = false;
    // Distinguish expected local closes (cleanup/completion/abstain) from
    // unexpected connection failures while the task is still running.
    let intentionalClose = false;
    const socket = new WebSocket(`${WEBSOCKET_URL}/doc/updates/${doc.id}`);

    // Clear any existing timeout when establishing new connection
    if (processingTimeoutRef.current) {
      clearTimeout(processingTimeoutRef.current);
      processingTimeoutRef.current = null;
    }

    socket.onopen = () => {
      if (disposed || intentionalClose) {
        intentionalClose = true;
        socket.close();
      }
    };

    socket.onmessage = (event) => {
      if (disposed) {
        return;
      }

      // console.log('WebSocket message received:', event.data);
      // Clear existing timeout when we receive a message
      if (processingTimeoutRef.current) {
        clearTimeout(processingTimeoutRef.current);
        processingTimeoutRef.current = null;
      }

      switch (event.data) {
        case BgTaskStatus.Reading:
          setAnimatedStatus('Reading post');
          break;
        case BgTaskStatus.ReadingThread:
          setAnimatedStatus('Reading thread');
          break;
        case BgTaskStatus.Abstain:
          setProcessingStatus({ message: 'Decided not to reply', animate: false });
          intentionalClose = true;
          socket.close();
          break;
        case BgTaskStatus.Researching:
          setAnimatedStatus('Researching');
          break;
        case BgTaskStatus.SearchingProducts:
          setAnimatedStatus('Searching products');
          break;
        case BgTaskStatus.AnalyzingSearchResults:
          setAnimatedStatus('Analyzing results');
          break;
        case BgTaskStatus.FetchingProductDetails:
          setAnimatedStatus('Fetching product details');
          break;
        case BgTaskStatus.FetchingReviews:
          setAnimatedStatus('Fetching reviews');
          break;
        case BgTaskStatus.LoadingSiteInfo:
          setAnimatedStatus('Loading site information');
          break;
        case BgTaskStatus.ReformulatingQuery:
          setAnimatedStatus('Refining search query');
          break;
        case BgTaskStatus.Recommending:
          setAnimatedStatus('Finding recommendations');
          break;
        case BgTaskStatus.Replying:
          setAnimatedStatus('Composing reply');
          break;
        case BgTaskStatus.Answered:
          setProcessingStatus({ message: 'Answered', animate: false });
          break;
        case BgTaskStatus.Replied:
          setProcessingStatus({ message: 'Replied', animate: false });
          break;
        case BgTaskStatus.Completed:
          setProcessingStatus({ message: null, animate: false }); // Clear status
          mutateRef.current(); // Reload the documents
          intentionalClose = true;
          socket.close();
          break;
        case BgTaskStatus.Failed:
          setProcessingStatus({ message: 'Internal error while replying', animate: false });
          intentionalClose = true;
          socket.close();
          break;
        case BgTaskStatus.ReformulatingProductTitle:
        case BgTaskStatus.WritingGuides:
          console.error('Unexpected non-post-processing status for doc updates: ', event.data);
          break;
        case BgTaskStatus.AnalyzingFeedback:
          setAnimatedStatus('Analyzing feedback');
          break;
        default:
          console.error('Unrecognized status: ', event.data);
          setProcessingStatus({ message: null, animate: false }); // Clear status
      }
    };

    socket.onerror = () => {
      // Avoid noisy console errors for expected startup/cleanup cases.
    };

    return () => {
      disposed = true;
      if (socket.readyState === WebSocket.OPEN) {
        intentionalClose = true;
        socket.close();
      }

      // Clear timeout on cleanup
      if (processingTimeoutRef.current) {
        clearTimeout(processingTimeoutRef.current);
        processingTimeoutRef.current = null;
      }
    };
  }, [doc.id, isMainDoc, socketGeneration]);

  // { console.log("DocCard: doc.topic is ", doc.topic) }

  const renderCardContent = () => (
    <div className={styles.flexItem}>
      <header className={styles.docMetadata}>
        <span className={styles.docCreator}>{doc.provenance.creator}</span>
        <time className={styles.docDate} dateTime={doc.provenance.createdon}>
          {new Date(doc.provenance.createdon).toLocaleString()}
        </time>
        <span className={styles.docIcons}>
          {userInfo && !isLLM(doc.provenance.creator) && (
            <VisibilityIcon className={styles.iconButton} visibility={doc.visibility} />
          )}
          {context === 'thread' && (
            <Link
              href={'/doc/' + doc.id}
              className={`${styles.noDecoration} ${styles.iconButton}`}
              aria-label="Permanent link to this post"
              title="Permanent link to this post"
              style={
                isMainDoc
                  ? {
                      background:
                        'linear-gradient(135deg, rgba(129, 69, 158, 0.2), rgba(9, 205, 205, 0.2))',
                      borderRadius: '50%',
                      padding: '4px',
                    }
                  : undefined
              }
            >
              <LinkIcon />
            </Link>
          )}
          {userInfo && (
            <IconButton
              onClick={handleStarClick}
              size="small"
              aria-label={isInCollection ? 'Remove from favorites' : 'Add to favorites'}
              title={isInCollection ? 'Remove from favorites' : 'Add to favorites'}
            >
              {isInCollection ? <StarIcon /> : <StarBorderIcon />}
            </IconButton>
          )}
          {voteInfo && (
            <Box className={styles.voteControls}>
              <IconButton
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  void handleVoteClick('up');
                }}
                size="small"
                disabled={isOwnPost || isVoting}
                aria-label={isOwnPost ? 'Cannot vote on own post' : 'Upvote'}
                title={isOwnPost ? 'Cannot vote on own post' : 'Upvote'}
                sx={
                  voteInfo.user_vote === 'up'
                    ? { color: 'var(--mui-palette-success-main)' }
                    : undefined
                }
              >
                {voteInfo.user_vote === 'up' ? <ThumbUpAlt /> : <ThumbUpOffAlt />}
              </IconButton>
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, minWidth: '1.5em', textAlign: 'center', userSelect: 'none' }}
              >
                {formatScore(voteInfo.score)}
              </Typography>
              <IconButton
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  void handleVoteClick('down');
                }}
                size="small"
                disabled={isOwnPost || isVoting}
                aria-label={isOwnPost ? 'Cannot vote on own post' : 'Downvote'}
                title={isOwnPost ? 'Cannot vote on own post' : 'Downvote'}
                sx={
                  voteInfo.user_vote === 'down'
                    ? { color: 'var(--mui-palette-error-main)' }
                    : undefined
                }
              >
                {voteInfo.user_vote === 'down' ? <ThumbDownAlt /> : <ThumbDownOffAlt />}
              </IconButton>
            </Box>
          )}
        </span>
      </header>

      {/* TODO(2): put this in a const somewhere, or translate it in the FE/BE API */}
      {doc.topic && (doc.aspect?.cref == ASPECT_REVIEW || doc.aspect?.cref == ASPECT_QUESTION) && (
        <div className={styles.docTopic}>
          {doc.topic.img_url && (
            <Link
              href={`/products/${encodeURIComponent(doc.topic.cref)}`}
              className={styles.noDecoration}
            >
              <Image
                src={doc.topic.img_url}
                alt={doc.topic.display}
                priority
                width={200}
                height={200}
                className={styles.docTopicImage}
              />
            </Link>
          )}
          <span className={styles.docTopicTitle}>
            <span>
              {doc.aspect?.cref == ASPECT_QUESTION
                ? 'Question on:'
                : doc.aspect?.cref == ASPECT_REVIEW
                  ? 'Review of:'
                  : ''}
              <br></br>
            </span>
            <span>
              {/* TODO(2): This assumes that all reviews and questions are on sku level products */}
              <Link
                href={`/products/${encodeURIComponent(doc.topic.cref)}`}
                className={styles.noDecoration}
              >
                {doc.topic.display}
              </Link>
            </span>
          </span>
        </div>
      )}

      <StructuredDocRenderer document={doc.structured_content!} concepts={doc.mentioned_concepts} />

      {context === 'thread' && (
        <div>
          {userInfo ? (
            <>
              {isComposeVisible && (
                <Box sx={{ position: 'relative', zIndex: 1300 }}>
                  <ComposeBox
                    onClose={() => {
                      setIsComposeVisible(false);
                      setIsComposing(false);
                    }}
                    topic={doc.topic?.cref || ''}
                    citationId={doc.id}
                    inheritedVisibility={inheritedVisibility}
                  />
                </Box>
              )}
              {!isComposeVisible && !(processingStatus.message && processingStatus.animate) && (
                <>
                  <Box
                    className={styles.composeControls}
                    sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}
                  >
                    {/* Compose Pathway #2: Inline Reply buttons manage local state directly
                        Unlike OmniBox clicks (which use triggerComposeAtLatest + replyToDocId for
                         cross-component coordination), inline Reply buttons just manage local state.
                        Both pathways set isComposing=true for UI state (hiding floating widgets). */}
                    <Button
                      onClick={() => {
                        setIsComposeVisible(true);
                        setIsComposing(true);
                      }}
                      aria-label="Reply to this post"
                      variant="outlined"
                      size="small"
                      startIcon={<ReplyIcon />}
                    >
                      Reply
                    </Button>
                    {!isLLM(doc.provenance.creator) && (
                      <Button
                        onClick={async () => {
                          try {
                            const response = await fetch(`${API_URL}/doc/trigger_reply/${doc.id}`, {
                              credentials: 'include',
                              method: 'POST',
                            });
                            if (!response.ok) {
                              throw new Error('Failed to trigger reply');
                            }

                            if (isMainDoc) {
                              // The main doc card already owns the live status UI, so
                              // update it in place and force the websocket effect to
                              // reconnect for this newly triggered task. This repeats
                              // the same initial status string used by the
                              // `processing_started` path above because both entry
                              // points intentionally surface the same immediate ack.
                              setAnimatedStatus('Starting AI analysis');
                              setSocketGeneration((count) => count + 1);
                            } else {
                              // For non-main-doc cards, navigate to the standalone doc
                              // page so that page's main-card websocket can take over.
                              router.replace(`/doc/${doc.id}?processing_started=1`);
                            }
                          } catch (error) {
                            console.error('Error triggering reply:', error);
                          }
                        }}
                        aria-label="Request AI response to this post"
                        title="Request AI response to this post"
                        variant="outlined"
                        size="small"
                        startIcon={<AutoFixHighIcon />}
                      >
                        AI Reply
                      </Button>
                    )}
                  </Box>
                </>
              )}
            </>
          ) : (
            isMainDoc && (
              <Box
                className={styles.composeControls}
                sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 2 }}
              >
                <Button
                  onClick={() => {
                    const currentPath = encodeURIComponent(window.location.pathname);
                    router.push(`/login?redirect=${currentPath}`);
                  }}
                  aria-label="Log in to reply to this post"
                  variant="contained"
                  size="large"
                >
                  Log in to Reply
                </Button>
              </Box>
            )
          )}

          {/* Show suggested questions if AI created this doc and has questions
              AND it's either the main doc or a direct child of the main doc */}
          {userInfo &&
            isLLM(doc.provenance.creator) &&
            doc.suggested_questions?.length > 0 &&
            (isMainDoc || isMainDocChild) && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                {doc.suggested_questions.map((question, index) => (
                  <Chip
                    key={index}
                    label={question}
                    size="small"
                    variant="outlined"
                    onClick={async () => {
                      try {
                        const response = await fetch(`${API_URL}/doc/new`, {
                          credentials: 'include',
                          method: 'POST',
                          headers: {
                            'Content-Type': 'application/json',
                          },
                          body: JSON.stringify({
                            content: question,
                            topic: doc.topic?.cref,
                            visibility: inheritedVisibility || DOC_VISIBILITY.Public,
                            citation: doc.id,
                          }),
                        });

                        if (!response.ok) {
                          throw new Error('Failed to post question');
                        }

                        const responseData = await response.json();
                        router.push(`/doc/${responseData.doc_id}?processing_started=1`);
                      } catch (error) {
                        console.error('Error posting suggested question:', error);
                      }
                    }}
                  />
                ))}
              </Box>
            )}
        </div>
      )}
      {processingStatus.message && (
        <div
          className={styles.processingStatus}
          data-ellipsis={processingStatus.animate ? '' : undefined}
          aria-live="polite"
          role="status"
        >
          Processing: {processingStatus.message}
        </div>
      )}
      {context === 'standalone' && (
        <Box className={styles.docThreadLink} sx={{ mt: 2, textAlign: 'right' }}>
          <MuiLink component={Link} href={`/doc/${doc.id}`} underline="none">
            Go to thread →
          </MuiLink>
        </Box>
      )}
    </div>
  );

  return (
    <article ref={cardRef}>
      {threadMarker}
      {indentationElement}

      {context === 'standalone' && clickable ? (
        <LinkCard
          href={`/doc/${doc.id}`}
          sx={
            doc.is_feedback_post || isInFeedbackSubtree
              ? {
                  opacity: 0.75,
                  backgroundColor: 'action.hover',
                }
              : undefined
          }
        >
          {renderCardContent()}
        </LinkCard>
      ) : (
        <InfoCard
          sx={
            doc.is_feedback_post || isInFeedbackSubtree
              ? {
                  opacity: 0.75,
                  backgroundColor: 'action.hover',
                }
              : undefined
          }
        >
          {renderCardContent()}
        </InfoCard>
      )}
    </article>
  );
};

export default DocCard;
